import React, { useState, useMemo, useEffect } from 'react';
import {
  useGetEnveloppesQuery, useRetirerEnveloppeMutation, useDeposerEnveloppeMutation,
  useGenererDepuisRelevesMutation, useGetStatsEnveloppesQuery, useGenererRecuMutation,
  useGetRecusQuery, useAnnulerRecuMutation, useChangerStatutEnveloppeMutation,
  useDeleteEnveloppeMutation, useSupprimerDoublonRecuMutation, useDetecterDoublonsRecusQuery,
  Enveloppe,
} from '../../services/enveloppesApi';
import {
  Package, Search, RefreshCw, ArrowRight, CheckCircle2, Clock, FileText,
  Printer, X, AlertTriangle, Wallet, Trash2, Receipt, TrendingUp, FileSpreadsheet, Copy,
} from 'lucide-react';
import ScrollToTop from '../../components/ui/ScrollToTop';

const STATUT_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  prete: { label: 'Prete', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', icon: <Package size={12} /> },
  retiree: { label: 'Retiree', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300', icon: <ArrowRight size={12} /> },
  en_correction: { label: 'En correction', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300', icon: <Clock size={12} /> },
  deposee: { label: 'Deposee', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', icon: <CheckCircle2 size={12} /> },
  facturee: { label: 'Facturee', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300', icon: <Wallet size={12} /> },
};

type TabType = 'enveloppes' | 'recus';

const SuiviEnveloppes: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('enveloppes');
  const [search, setSearch] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');
  const [filtreSession, setFiltreSession] = useState<'' | 'normal' | 'rattrapage'>('');
  // Sélecteur GLOBAL du semestre (partagé entre onglets Enveloppes + Reçus émis) —
  // persisté en localStorage pour ne pas re-choisir à chaque navigation.
  const [filtreSemestreParity, setFiltreSemestreParity] = useState<'' | 'impair' | 'pair'>(
    () => (localStorage.getItem('enveloppes_semestre_parity') as any) || ''
  );
  useEffect(() => {
    try { localStorage.setItem('enveloppes_semestre_parity', filtreSemestreParity); } catch { /* ignore */ }
  }, [filtreSemestreParity]);
  const [recuSearch, setRecuSearch] = useState('');
  const [recuDateDebut, setRecuDateDebut] = useState('');
  const [recuDateFin, setRecuDateFin] = useState('');
  const [recuSession, setRecuSession] = useState<'' | 'normal' | 'rattrapage' | 'mixte'>('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Helper parité semestre
  const parityOf = (sem: any): 'impair' | 'pair' | null => {
    const n = parseInt(String(sem || '').replace(/\D/g, ''), 10);
    if (isNaN(n) || n === 0) return null;
    return n % 2 === 1 ? 'impair' : 'pair';
  };

  const { data: enveloppes = [], isLoading, refetch } = useGetEnveloppesQuery({
    search: search || undefined,
    statut: filtreStatut || undefined,
  });
  const { data: stats } = useGetStatsEnveloppesQuery();
  const { data: recus = [] } = useGetRecusQuery();
  const { data: doublonsData, refetch: refetchDoublons } = useDetecterDoublonsRecusQuery(undefined, { skip: activeTab !== 'recus' });
  const [retirer] = useRetirerEnveloppeMutation();
  const [deposer] = useDeposerEnveloppeMutation();
  const [genererDepuis, { isLoading: generant }] = useGenererDepuisRelevesMutation();
  const [genererRecu, { isLoading: generantRecu }] = useGenererRecuMutation();
  const [annulerRecu] = useAnnulerRecuMutation();
  const [supprimerDoublon] = useSupprimerDoublonRecuMutation();
  const [changerStatut] = useChangerStatutEnveloppeMutation();
  const [deleteEnveloppe] = useDeleteEnveloppeMutation();
  const [showDoublonsModal, setShowDoublonsModal] = useState(false);

  // IDs des reçus considérés comme doublons (pour mise en évidence visuelle)
  const recusDoublonsIds = useMemo(() => {
    const ids = new Set<number>();
    if (doublonsData?.groupes) {
      for (const g of doublonsData.groupes) {
        // Marquer tous sauf le premier (le plus ancien) comme doublons
        g.recus.slice(1).forEach(r => ids.add(r.id));
      }
    }
    return ids;
  }, [doublonsData]);

  const handleRetirer = async (env: Enveloppe) => {
    if (!window.confirm(`Confirmer le retrait de l'enveloppe ${env.reference} par ${env.formateur_nom} ?`)) return;
    try {
      await retirer({ id: env.id }).unwrap();
      setMessage({ type: 'success', text: `Enveloppe ${env.reference} retiree par ${env.formateur_nom}` });
      refetch();
    } catch (err: any) { setMessage({ type: 'error', text: err?.data?.error || 'Erreur' }); }
  };

  const handleDeposer = async (env: Enveloppe) => {
    if (!window.confirm(`Confirmer le depot de l'enveloppe ${env.reference} ?`)) return;
    try {
      await deposer({ id: env.id }).unwrap();
      setMessage({ type: 'success', text: `Enveloppe ${env.reference} deposee` });
      refetch();
    } catch (err: any) { setMessage({ type: 'error', text: err?.data?.error || 'Erreur' }); }
  };

  const handleGenererDepuis = async () => {
    try {
      const res = await genererDepuis().unwrap();
      setMessage({ type: 'success', text: res.message });
      refetch();
    } catch (err: any) { setMessage({ type: 'error', text: err?.data?.error || 'Erreur' }); }
  };

  // Ouverture d'un modal pré-génération pour :
  //  1. saisir d'éventuelles copies supplémentaires (années antérieures)
  //  2. voir un résumé + montant estimé avant validation
  const [modalGenerer, setModalGenerer] = useState<{
    formateurNom: string;
    formateurId: number | null;
    enveloppeIds: number[];
    nbCopiesEnveloppes: number;
  } | null>(null);

  const handleGenererRecu = (formateurNom: string, formateurId: number | null, enveloppeIds: number[]) => {
    const nbCopiesEnveloppes = enveloppes
      .filter((e: any) => enveloppeIds.includes(e.id))
      .reduce((s: number, e: any) => s + (e.nombre_copies || 0), 0);
    setModalGenerer({ formateurNom, formateurId, enveloppeIds, nbCopiesEnveloppes });
  };

  const confirmerGenererRecu = async (params: {
    copies_supplementaires: number;
    copies_supplementaires_motif: string;
    force_duplicata?: boolean;
  }) => {
    if (!modalGenerer) return;
    try {
      await genererRecu({
        formateur_id: modalGenerer.formateurId || undefined,
        enveloppe_ids: modalGenerer.enveloppeIds,
        copies_supplementaires: params.copies_supplementaires || 0,
        copies_supplementaires_motif: params.copies_supplementaires_motif || '',
        force_duplicata: !!params.force_duplicata,
      } as any).unwrap();
      setMessage({ type: 'success', text: `Reçu généré pour ${modalGenerer.formateurNom}` });
      setModalGenerer(null);
      refetch();
    } catch (err: any) {
      // 409 = doublon détecté → proposer d'émettre un duplicata
      if (err?.status === 409 && err?.data?.error === 'DOUBLON') {
        const info = err.data.recu_existant;
        const ok = window.confirm(
          `⚠ Un reçu identique existe déjà : ${info?.numero_recu}\n` +
          `${info?.total_copies} copies · ${info?.montant_total} FCFA\n\n` +
          `Voulez-vous émettre un DUPLICATA (nouveau numéro, marqué comme copie) ?`
        );
        if (ok) {
          confirmerGenererRecu({ ...params, force_duplicata: true });
          return;
        }
        setMessage({ type: 'error', text: `Génération annulée — reçu déjà émis : ${info?.numero_recu}` });
      } else {
        setMessage({ type: 'error', text: err?.data?.error || err?.data?.detail || 'Erreur' });
      }
    }
  };

  const handleAnnulerRecu = async (recuId: number, numero: string) => {
    if (!window.confirm(`Annuler le recu ${numero} ? Les enveloppes seront remises en statut "deposee".`)) return;
    try {
      const res = await annulerRecu(recuId).unwrap();
      setMessage({ type: 'success', text: res.message });
      refetch();
    } catch (err: any) { setMessage({ type: 'error', text: err?.data?.error || 'Erreur' }); }
  };

  const handleSupprimerDoublon = async (recuId: number, numero: string) => {
    if (!window.confirm(`SUPPRIMER définitivement le reçu doublon ${numero} ?\n\n⚠ Cette action :\n- Supprime le reçu sans toucher aux enveloppes\n- Est irréversible\n\nÀ utiliser uniquement quand un reçu identique existe déjà.`)) return;
    try {
      const res = await supprimerDoublon(recuId).unwrap();
      setMessage({ type: 'success', text: res.message });
      refetchDoublons();
    } catch (err: any) { setMessage({ type: 'error', text: err?.data?.error || 'Erreur' }); }
  };

  const handleDeleteEnveloppe = async (env: Enveloppe) => {
    if (!window.confirm(`Supprimer l'enveloppe ${env.reference} (${env.matiere_nom} - ${env.formateur_nom}) ?\n\nCette action est irreversible.`)) return;
    try {
      await deleteEnveloppe(env.id).unwrap();
      setMessage({ type: 'success', text: `Enveloppe ${env.reference} supprimee` });
      refetch();
    } catch (err: any) { setMessage({ type: 'error', text: err?.data?.error || err?.data?.detail || 'Erreur lors de la suppression' }); }
  };

  const handleChangerStatut = async (env: Enveloppe, nouveauStatut: string) => {
    const labels: Record<string, string> = { prete: 'Prete', retiree: 'Retiree', en_correction: 'En correction', deposee: 'Deposee', facturee: 'Facturee' };
    if (!window.confirm(`Changer le statut de ${env.reference} vers "${labels[nouveauStatut]}" ?`)) return;
    try {
      const res = await changerStatut({ id: env.id, statut: nouveauStatut }).unwrap();
      setMessage({ type: 'success', text: res.message });
      refetch();
    } catch (err: any) { setMessage({ type: 'error', text: err?.data?.error || 'Erreur' }); }
  };

  const handleImprimerRecu = async (recuId: number) => {
    const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:8000' : '';
    const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
    const annee = localStorage.getItem('annee_academique');
    try {
      const response = await fetch(`${baseUrl}/api/recus-correction/${recuId}/imprimer_recu/`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(annee ? { 'X-Annee-Academique': annee } : {}),
        },
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      } else {
        setMessage({ type: 'error', text: 'Erreur lors de l\'impression du recu' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Erreur de connexion' });
    }
  };

  // Filtrage des reçus selon les filtres
  const recusFiltres = useMemo(() => {
    return recus.filter((r: any) => {
      if (recuSearch) {
        const q = recuSearch.toLowerCase();
        if (!r.numero_recu.toLowerCase().includes(q) &&
            !(r.formateur_nom || '').toLowerCase().includes(q)) return false;
      }
      if (recuDateDebut && r.date_emission < recuDateDebut) return false;
      if (recuDateFin && r.date_emission > recuDateFin + 'T23:59:59') return false;
      if (recuSession && (r.type_examen || 'normal') !== recuSession) return false;
      // Sélecteur GLOBAL de parité — s'applique aussi aux reçus émis.
      // Le champ `semestre_parite` du reçu est renseigné à la génération
      // depuis les enveloppes liées ; les reçus historiques sans valeur sont
      // ignorés du filtre (masqués) uniquement si un filtre est actif.
      if (filtreSemestreParity) {
        if ((r.semestre_parite || '') !== filtreSemestreParity) return false;
      }
      return true;
    });
  }, [recus, recuSearch, recuDateDebut, recuDateFin, recuSession, filtreSemestreParity]);

  // Total des reçus émis (séparé par session pour clarté)
  const totalRecus = useMemo(() => {
    const sumOf = (arr: any[]) => ({
      totalGeneral: arr.reduce((s, r) => s + (r.montant_total || 0), 0),
      totalCopies: arr.reduce((s, r) => s + (r.total_copies || 0), 0),
      totalBonus: arr.reduce((s, r) => s + (r.bonus || 0), 0),
      formateursDistincts: new Set(arr.map(r => r.formateur_nom)).size,
      nbRecus: arr.length,
    });
    const all = sumOf(recusFiltres);
    const ordinaire = sumOf(recusFiltres.filter((r: any) => (r.type_examen || 'normal') === 'normal'));
    const rattrapage = sumOf(recusFiltres.filter((r: any) => r.type_examen === 'rattrapage'));
    return { ...all, ordinaire, rattrapage };
  }, [recusFiltres]);

  const handleImprimerSynthese = () => {
    // Génération HTML de la synthèse à imprimer
    const w = window.open('', '_blank');
    if (!w) return;
    const annee = localStorage.getItem('annee_academique') || '2025-2026';
    const dateNow = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
    const periode = (recuDateDebut || recuDateFin)
      ? `Période : ${recuDateDebut ? new Date(recuDateDebut).toLocaleDateString('fr-FR') : '...'} au ${recuDateFin ? new Date(recuDateFin).toLocaleDateString('fr-FR') : '...'}`
      : 'Toute la période';

    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>Synthèse des reçus de correction</title>
      <style>
        @page { size: A4; margin: 15mm; }
        body { font-family: 'Helvetica', Arial, sans-serif; color: #1f2937; font-size: 11pt; }
        h1 { color: #1e40af; font-size: 16pt; margin: 0; }
        h2 { color: #4b5563; font-size: 11pt; margin: 4px 0 0; font-weight: normal; }
        .header { text-align: center; border-bottom: 3px solid #1e40af; padding-bottom: 12px; margin-bottom: 16px; }
        .meta { display: flex; justify-content: space-between; font-size: 9pt; color: #6b7280; margin-bottom: 12px; }
        .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 16px; }
        .stat-card { border: 1px solid #d1d5db; border-radius: 6px; padding: 10px; text-align: center; background: #f9fafb; }
        .stat-label { font-size: 8pt; color: #6b7280; text-transform: uppercase; font-weight: 600; }
        .stat-value { font-size: 16pt; font-weight: bold; margin-top: 4px; }
        .blue { color: #2563eb; }
        .green { color: #16a34a; }
        .purple { color: #9333ea; }
        .orange { color: #ea580c; }
        table { width: 100%; border-collapse: collapse; font-size: 9pt; }
        th { background: #1e40af; color: white; padding: 6px; text-align: left; font-weight: 600; }
        td { padding: 5px 6px; border-bottom: 1px solid #e5e7eb; }
        tr:nth-child(even) td { background: #f9fafb; }
        .total-row td { background: #fef3c7 !important; font-weight: bold; font-size: 11pt; padding: 8px 6px; border-top: 2px solid #f59e0b; }
        .signature-block { margin-top: 30px; display: flex; justify-content: space-between; }
        .signature-box { width: 45%; }
        .signature-box .label { font-size: 9pt; color: #6b7280; margin-bottom: 30px; }
        .signature-box .line { border-top: 1px solid #9ca3af; padding-top: 4px; font-size: 9pt; }
        .footer { margin-top: 16px; font-size: 8pt; color: #9ca3af; text-align: center; border-top: 1px solid #e5e7eb; padding-top: 6px; }
        @media print { .no-print { display: none; } }
      </style></head><body>
      <div class="header">
        <h1>SYNTHÈSE DES REÇUS DE CORRECTION</h1>
        <h2>Institut International 2i — Année académique ${annee}</h2>
      </div>
      <div class="meta">
        <span><strong>${periode}</strong></span>
        <span>Édité le ${dateNow}</span>
      </div>
      <div class="stats">
        <div class="stat-card"><div class="stat-label">Reçus émis</div><div class="stat-value blue">${totalRecus.nbRecus}</div></div>
        <div class="stat-card"><div class="stat-label">Formateurs</div><div class="stat-value purple">${totalRecus.formateursDistincts}</div></div>
        <div class="stat-card"><div class="stat-label">Total copies</div><div class="stat-value orange">${totalRecus.totalCopies.toLocaleString()}</div></div>
        <div class="stat-card"><div class="stat-label">Montant total</div><div class="stat-value green">${totalRecus.totalGeneral.toLocaleString()} FCFA</div></div>
      </div>
      <table>
        <thead>
          <tr>
            <th>N° Reçu</th>
            <th>Date</th>
            <th>Formateur</th>
            <th style="text-align:center">Copies</th>
            <th style="text-align:right">Bonus</th>
            <th style="text-align:right">Montant</th>
          </tr>
        </thead>
        <tbody>
          ${recusFiltres.map(r => `<tr>
            <td><strong>${r.numero_recu}</strong></td>
            <td>${new Date(r.date_emission).toLocaleDateString('fr-FR')}</td>
            <td>${r.formateur_nom || '—'}</td>
            <td style="text-align:center">${r.total_copies}</td>
            <td style="text-align:right">${r.bonus ? r.bonus.toLocaleString() : '—'}</td>
            <td style="text-align:right"><strong>${r.montant_total.toLocaleString()} FCFA</strong></td>
          </tr>`).join('')}
          <tr class="total-row">
            <td colspan="3">TOTAL GÉNÉRAL</td>
            <td style="text-align:center">${totalRecus.totalCopies.toLocaleString()}</td>
            <td style="text-align:right">${totalRecus.totalBonus.toLocaleString()}</td>
            <td style="text-align:right">${totalRecus.totalGeneral.toLocaleString()} FCFA</td>
          </tr>
        </tbody>
      </table>
      <div class="signature-block">
        <div class="signature-box">
          <div class="label">Préparé par (Scolarité)</div>
          <div class="line">Nom & Signature</div>
        </div>
        <div class="signature-box">
          <div class="label">Approuvé par (Caisse / Direction)</div>
          <div class="line">Nom & Signature</div>
        </div>
      </div>
      <div class="footer">
        Document généré automatiquement — Système de Gestion des Notes 2i
      </div>
      <script>setTimeout(()=>window.print(), 300);</script>
      </body></html>`);
    w.document.close();
  };

  const handleExportRecusExcel = () => {
    // Export CSV simple (Excel-compatible)
    const headers = ['N° Reçu', 'Date', 'Formateur', 'Copies', 'Bonus', 'Montant'];
    const rows = recusFiltres.map(r => [
      r.numero_recu,
      new Date(r.date_emission).toLocaleDateString('fr-FR'),
      r.formateur_nom || '',
      r.total_copies,
      r.bonus || 0,
      r.montant_total,
    ]);
    rows.push(['TOTAL', '', '', totalRecus.totalCopies, totalRecus.totalBonus, totalRecus.totalGeneral]);
    const csv = [headers, ...rows].map(line => line.map(c => `"${c}"`).join(';')).join('\n');
    const bom = '\uFEFF'; // pour Excel UTF-8
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recus_correction_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Liste filtrée côté client : type session + parité semestre
  const enveloppesFiltrees = useMemo(() => {
    let res = enveloppes;
    if (filtreSession) res = res.filter((e: any) => (e.type_examen || 'normal') === filtreSession);
    if (filtreSemestreParity) res = res.filter((e: any) => parityOf(e.semestre) === filtreSemestreParity);
    return res;
  }, [enveloppes, filtreSession, filtreSemestreParity]);

  // Grouper les enveloppes par formateur ET par session (ordinaire vs rattrapage)
  // pour facturer séparément les 2 sessions, comme demandé par la scolarité.
  const parFormateur: Record<string, { nom: string; id: number | null; type_examen: string; deposees: Enveloppe[]; en_correction: Enveloppe[] }> = {};
  enveloppesFiltrees.filter(e => e.statut === 'deposee' || e.statut === 'en_correction' || e.statut === 'retiree').forEach((e: any) => {
    const tex = e.type_examen || 'normal';
    const key = `${e.formateur_nom || 'Inconnu'}|${tex}`;
    if (!parFormateur[key]) parFormateur[key] = {
      nom: e.formateur_nom || 'Inconnu',
      id: e.formateur_id,
      type_examen: tex,
      deposees: [], en_correction: [],
    };
    if (e.statut === 'deposee') parFormateur[key].deposees.push(e);
    else parFormateur[key].en_correction.push(e);
  });
  // Ne garder que les formateurs qui ont au moins une enveloppe deposee
  const formateursAFacturer = Object.values(parFormateur).filter(f => f.deposees.length > 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="mb-4 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
            <Package size={28} className="text-indigo-600" /> Suivi des Enveloppes & Reçus
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Gestion des enveloppes de correction et suivi financier
          </p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'enveloppes' && (
            <button onClick={handleGenererDepuis} disabled={generant}
              className="flex items-center px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
              {generant ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Package size={16} className="mr-2" />}
              Generer depuis releves
            </button>
          )}
          {/* Export Excel de tous les reçus filtrés (2 feuilles : détails + synthèse formateur).
              Applique les filtres actifs (session, semestre) via query string. */}
          <button
            onClick={() => {
              const params = new URLSearchParams();
              if (filtreSession) params.set('type_examen', filtreSession);
              if (filtreSemestreParity) params.set('semestre_parite', filtreSemestreParity);
              const url = `${window.location.origin.replace(/\/$/,'')}/api/recus-correction/export-excel/?${params.toString()}`;
              window.open(url, '_blank');
            }}
            className="flex items-center px-3 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
            title="Exporter tous les reçus filtrés en Excel (détails + synthèse par formateur)"
          >
            <FileText size={16} className="mr-2" /> Export Excel
          </button>
          <button onClick={() => refetch()} className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600" title="Actualiser">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Sélecteur GLOBAL du semestre — visible en haut, s'applique aux 2 onglets
          (Enveloppes ET Reçus émis). Persisté en localStorage pour respecter
          le choix de l'agent entre navigations. */}
      {(() => {
        // Compteurs vivants basés sur toutes les enveloppes chargées
        const parityOfLocal = (sem: any): 'impair' | 'pair' | null => {
          const n = parseInt(String(sem || '').replace(/\D/g, ''), 10);
          if (isNaN(n) || n === 0) return null;
          return n % 2 === 1 ? 'impair' : 'pair';
        };
        const nbEnvImpair = enveloppes.filter((e: any) => parityOfLocal(e.semestre) === 'impair').length;
        const nbEnvPair = enveloppes.filter((e: any) => parityOfLocal(e.semestre) === 'pair').length;
        const nbRecuImpair = recus.filter((r: any) => (r.semestre_parite || '') === 'impair').length;
        const nbRecuPair = recus.filter((r: any) => (r.semestre_parite || '') === 'pair').length;
        return (
          <div className="mb-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Semestre :
            </span>
            <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
              <button
                onClick={() => setFiltreSemestreParity('')}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                  !filtreSemestreParity
                    ? 'bg-gray-800 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
                title="Afficher tous les semestres"
              >
                Tous
                <span className="ml-1.5 opacity-70">({enveloppes.length}E · {recus.length}R)</span>
              </button>
              <button
                onClick={() => setFiltreSemestreParity('impair')}
                className={`px-3 py-1.5 text-xs font-semibold border-l border-gray-300 dark:border-gray-600 transition-colors ${
                  filtreSemestreParity === 'impair'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-emerald-50 dark:hover:bg-emerald-900/30'
                }`}
                title="Semestres impairs (S1 / S3 / S5)"
              >
                Impairs (S1/S3/S5)
                <span className="ml-1.5 opacity-70">({nbEnvImpair}E · {nbRecuImpair}R)</span>
              </button>
              <button
                onClick={() => setFiltreSemestreParity('pair')}
                className={`px-3 py-1.5 text-xs font-semibold border-l border-gray-300 dark:border-gray-600 transition-colors ${
                  filtreSemestreParity === 'pair'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-purple-50 dark:hover:bg-purple-900/30'
                }`}
                title="Semestres pairs (S2 / S4 / S6)"
              >
                Pairs (S2/S4/S6)
                <span className="ml-1.5 opacity-70">({nbEnvPair}E · {nbRecuPair}R)</span>
              </button>
            </div>
            {filtreSemestreParity && (
              <span className="text-[11px] text-gray-500 dark:text-gray-400 italic">
                Filtre appliqué à <strong>Enveloppes</strong> ET <strong>Reçus émis</strong>
              </span>
            )}
            <span className="ml-auto text-[10px] text-gray-400 italic">
              E = enveloppes · R = reçus
            </span>
          </div>
        );
      })()}

      {/* Onglets */}
      <div className="mb-4 flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl border border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('enveloppes')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'enveloppes'
              ? 'bg-white dark:bg-gray-700 text-indigo-700 dark:text-indigo-300 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
          }`}
        >
          <Package size={16} />
          Enveloppes
          {enveloppes.length > 0 && (
            <span className={`ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
              activeTab === 'enveloppes' ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
            }`}>{enveloppes.length}</span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('recus')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'recus'
              ? 'bg-white dark:bg-gray-700 text-purple-700 dark:text-purple-300 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
          }`}
        >
          <Receipt size={16} />
          Reçus émis
          {recus.length > 0 && (
            <span className={`ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
              activeTab === 'recus' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
            }`}>{recus.length}</span>
          )}
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 p-3 rounded-lg text-sm flex items-center justify-between ${message.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700'}`}>
          {message.text}
          <button onClick={() => setMessage(null)}><X size={14} /></button>
        </div>
      )}

      {/* ===== ONGLET ENVELOPPES ===== */}
      {activeTab === 'enveloppes' && (
        <>
          {/* Stats */}
          {stats && (
            <div className="mb-6 grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { label: 'Total', value: stats.total, color: 'text-gray-900 dark:text-gray-100' },
                { label: 'Pretes', value: stats.pretes, color: 'text-blue-600' },
                { label: 'En correction', value: stats.en_correction, color: 'text-yellow-600' },
                { label: 'Deposees', value: stats.deposees, color: 'text-green-600' },
                { label: 'Facturees', value: stats.facturees, color: 'text-purple-600' },
              ].map(s => (
                <div key={s.label} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{s.label}</div>
                  <div className={`text-2xl font-extrabold mt-1 ${s.color}`}>{s.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Budget : séparation Ordinaire / Rattrapage */}
          {stats && (stats.session_ordinaire || stats.session_rattrapage) && (
            <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                {
                  key: 'normal',
                  label: 'Session Ordinaire',
                  data: stats.session_ordinaire,
                  bg: 'bg-emerald-50 dark:bg-emerald-900/10',
                  border: 'border-emerald-200 dark:border-emerald-800',
                  text: 'text-emerald-700 dark:text-emerald-300',
                  accent: 'text-emerald-600',
                },
                {
                  key: 'rattrapage',
                  label: 'Session de Rattrapage',
                  data: stats.session_rattrapage,
                  bg: 'bg-red-50 dark:bg-red-900/10',
                  border: 'border-red-200 dark:border-red-800',
                  text: 'text-red-700 dark:text-red-300',
                  accent: 'text-red-600',
                },
                {
                  key: 'total',
                  label: 'Total',
                  data: stats.total_budget,
                  bg: 'bg-indigo-50 dark:bg-indigo-900/10',
                  border: 'border-indigo-200 dark:border-indigo-800',
                  text: 'text-indigo-700 dark:text-indigo-300',
                  accent: 'text-indigo-600',
                },
              ].map(b => b.data ? (
                <div key={b.key} className={`${b.bg} ${b.border} border rounded-xl p-4`}>
                  <div className={`text-xs font-semibold uppercase tracking-wide ${b.text}`}>
                    Budget — {b.label}
                  </div>
                  <div className="mt-2 space-y-0.5 text-[11px] text-gray-600 dark:text-gray-400">
                    <div>{b.data.nb_enveloppes} enveloppe(s) · {b.data.copies_deposees + b.data.copies_en_cours} copies au total</div>
                    <div>Déposées : <strong className="text-gray-900 dark:text-gray-100">{b.data.copies_deposees}</strong> · En cours : <strong className="text-gray-900 dark:text-gray-100">{b.data.copies_en_cours}</strong></div>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <div className="text-gray-500 dark:text-gray-400">Réel</div>
                      <div className={`text-lg font-extrabold ${b.accent}`}>{b.data.budget_reel.toLocaleString('fr-FR')}</div>
                      <div className="text-[9px] text-gray-500">FCFA (déposées)</div>
                    </div>
                    <div>
                      <div className="text-gray-500 dark:text-gray-400">Estimatif</div>
                      <div className={`text-lg font-extrabold ${b.accent} opacity-70`}>{b.data.budget_estimatif.toLocaleString('fr-FR')}</div>
                      <div className="text-[9px] text-gray-500">FCFA (si tout déposé)</div>
                    </div>
                  </div>
                </div>
              ) : null)}
            </div>
          )}

      {/* Tableau de bord par formateur (enveloppes en correction) */}
      {stats && stats.par_formateur && stats.par_formateur.filter(f => f.en_correction > 0).length > 0 && (
        <div className="mb-6 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800 rounded-xl p-4">
          <h3 className="text-sm font-bold text-orange-800 dark:text-orange-300 flex items-center gap-2 mb-3">
            <AlertTriangle size={16} /> Enveloppes en cours de correction
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {stats.par_formateur.filter(f => f.en_correction > 0).map(f => (
              <div key={f.formateur_nom} className="bg-white dark:bg-gray-800 rounded-lg border border-orange-200 dark:border-orange-700 p-3">
                <div className="font-semibold text-sm text-gray-900 dark:text-gray-100">{f.formateur_nom}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {f.en_correction} enveloppe(s) | {f.copies_total} copies
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Facturation : enveloppes deposees groupees par formateur */}
      {formateursAFacturer.length > 0 && (
        <div className="mb-6 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-xl p-4">
          <h3 className="text-sm font-bold text-green-800 dark:text-green-300 flex items-center gap-2 mb-3">
            <Wallet size={16} /> A facturer ({formateursAFacturer.length} formateur(s))
          </h3>
          <div className="space-y-3">
            {formateursAFacturer.map(f => {
              const totalCopiesDeposees = f.deposees.reduce((s, e) => s + e.nombre_copies, 0);
              const totalCopiesEnCours = f.en_correction.reduce((s, e) => s + e.nombre_copies, 0);
              const toutDepose = f.en_correction.length === 0;
              const totalSiTout = totalCopiesDeposees + totalCopiesEnCours;
              const montantDeposees = (totalCopiesDeposees * 100) + (totalCopiesDeposees < 10 ? 1000 : 0);
              return (
                <div key={`${f.nom}-${f.type_examen}`} className={`bg-white dark:bg-gray-800 rounded-lg border p-3 ${toutDepose ? 'border-green-300 dark:border-green-700' : 'border-orange-300 dark:border-orange-700'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 flex items-center gap-2 flex-wrap">
                        {f.nom}
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${
                          f.type_examen === 'rattrapage'
                            ? 'bg-red-600 text-white'
                            : 'bg-emerald-600 text-white'
                        }`}>
                          {f.type_examen === 'rattrapage' ? 'Rattrapage' : 'Ordinaire'}
                        </span>
                      </div>

                      {/* Enveloppes deposees */}
                      <div className="text-xs text-green-700 dark:text-green-400 mt-1">
                        {f.deposees.length} deposee(s) — {totalCopiesDeposees} copies — <strong>{montantDeposees.toLocaleString()} FCFA</strong>
                        {totalCopiesDeposees < 10 && <span className="ml-1">(bonus inclus)</span>}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {f.deposees.map(e => (
                          <span key={e.id} className="text-[9px] px-1.5 py-0.5 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded border border-green-200 dark:border-green-800">
                            {e.matiere_nom} ({e.nombre_copies})
                          </span>
                        ))}
                      </div>

                      {/* Avertissement enveloppes encore en correction */}
                      {f.en_correction.length > 0 && (
                        <div className="mt-2 p-2 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800 rounded">
                          <div className="text-[10px] text-orange-700 dark:text-orange-300 font-medium flex items-center gap-1">
                            <AlertTriangle size={12} /> {f.en_correction.length} enveloppe(s) encore en correction ({totalCopiesEnCours} copies)
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {f.en_correction.map(e => (
                              <span key={e.id} className="text-[9px] px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 rounded">
                                {e.matiere_nom} ({e.nombre_copies})
                              </span>
                            ))}
                          </div>
                          <div className="text-[9px] text-orange-500 mt-1">
                            Total si toutes deposees : {totalSiTout} copies = {((totalSiTout * 100) + (totalSiTout < 10 ? 1000 : 0)).toLocaleString()} FCFA
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-1 shrink-0">
                      {/* Recu pour les deposees uniquement */}
                      <button
                        onClick={() => handleGenererRecu(f.nom, f.id, f.deposees.map(e => e.id))}
                        disabled={generantRecu || f.deposees.length === 0}
                        className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-[10px] font-medium hover:bg-green-700 disabled:opacity-50"
                      >
                        <Printer size={12} /> Recu ({f.deposees.length} env.)
                      </button>
                      {/* Récap PDF multi-mois pour ce formateur (à annexer au dossier de paie) */}
                      {f.id && (
                        <button
                          onClick={() => {
                            const url = `${window.location.origin.replace(/\/$/,'')}/api/recus-correction/recap-formateur/?formateur_id=${f.id}`;
                            window.open(url, '_blank');
                          }}
                          className="flex items-center gap-1 px-3 py-1.5 border border-indigo-500 text-indigo-600 dark:text-indigo-400 rounded-lg text-[10px] font-medium hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
                          title="Récapitulatif PDF de tous les reçus de ce formateur"
                        >
                          <FileText size={12} /> Récap PDF
                        </button>
                      )}
                      {/* Info */}
                      {!toutDepose && (
                        <span className="text-[9px] text-orange-500 text-center">
                          Attendre le depot complet ?
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="mb-4 flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher par reference, formateur, matiere..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
        </div>
        <select value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
          <option value="">Tous les statuts</option>
          {Object.entries(STATUT_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filtreSession} onChange={(e) => setFiltreSession(e.target.value as any)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          title="Session ordinaire ou rattrapage">
          <option value="">Toutes sessions</option>
          <option value="normal">Session ordinaire</option>
          <option value="rattrapage">Session de rattrapage</option>
        </select>
        {/* Le sélecteur semestre est désormais global, en tête de page.
            Il s'applique automatiquement aux 2 onglets. */}
      </div>

      {/* Liste des enveloppes */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-gray-400"><RefreshCw size={24} className="animate-spin mx-auto" /></div>
        ) : enveloppesFiltrees.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <Package size={32} className="mx-auto mb-2 opacity-50" />
            {enveloppes.length === 0
              ? <>Aucune enveloppe. Cliquez "Generer depuis releves" pour creer les enveloppes.</>
              : <>Aucune enveloppe ne correspond aux filtres.</>}
          </div>
        ) : (
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Reference</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Matiere</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Classes</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Session</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Formateur</th>
                <th className="px-3 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase">Copies</th>
                <th className="px-3 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase">Statut</th>
                <th className="px-3 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase">Dates</th>
                <th className="px-3 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {enveloppesFiltrees.map((env: any) => {
                const sc = STATUT_CONFIG[env.statut] || STATUT_CONFIG.prete;
                return (
                  <tr key={env.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-3 py-2 text-xs font-mono font-medium text-gray-800 dark:text-gray-200">{env.reference}</td>
                    <td className="px-3 py-2">
                      <div className="text-xs font-medium text-gray-900 dark:text-gray-100">{env.matiere_nom}</div>
                      <div className="text-[10px] text-gray-400">{env.matiere_code}</div>
                    </td>
                    <td className="px-3 py-2 text-[10px] text-gray-600 dark:text-gray-400 max-w-[120px] truncate" title={env.classes}>{env.classes}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${
                        env.type_examen === 'rattrapage'
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                          : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                      }`} title={`Semestre ${env.semestre || '?'}`}>
                        {env.type_examen === 'rattrapage' ? 'Ratt.' : 'Ord.'}
                        {env.semestre ? ` · S${env.semestre}` : ''}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300">{env.formateur_nom || '—'}</td>
                    <td className="px-3 py-2 text-center text-sm font-bold text-blue-600">{env.nombre_copies}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${sc.color}`}>
                        {sc.icon} {sc.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-[10px] text-gray-500 text-center">
                      {env.date_retrait && <div>Retrait: {new Date(env.date_retrait).toLocaleDateString('fr-FR')}</div>}
                      {env.date_depot && <div>Depot: {new Date(env.date_depot).toLocaleDateString('fr-FR')}</div>}
                      {!env.date_retrait && !env.date_depot && <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        {env.statut === 'prete' && (
                          <button onClick={() => handleRetirer(env)} className="px-2 py-1 text-[10px] bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded hover:bg-orange-200 font-medium">
                            Retirer
                          </button>
                        )}
                        {(env.statut === 'retiree' || env.statut === 'en_correction') && (
                          <button onClick={() => handleDeposer(env)} className="px-2 py-1 text-[10px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded hover:bg-green-200 font-medium">
                            Deposer
                          </button>
                        )}
                        {/* Menu retour en arriere */}
                        <select
                          value=""
                          onChange={(e) => { if (e.target.value) handleChangerStatut(env, e.target.value); }}
                          className="px-1 py-0.5 text-[9px] border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                        >
                          <option value="">Statut...</option>
                          {['prete', 'retiree', 'en_correction', 'deposee'].filter(s => s !== env.statut).map(s => (
                            <option key={s} value={s}>← {s === 'prete' ? 'Prete' : s === 'retiree' ? 'Retiree' : s === 'en_correction' ? 'En correction' : 'Deposee'}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleDeleteEnveloppe(env)}
                          className="px-1.5 py-1 text-[10px] text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          title={`Supprimer ${env.reference}`}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

        </>
      )}

      {/* ===== ONGLET REÇUS ÉMIS ===== */}
      {activeTab === 'recus' && (
        <>
          {/* Alerte doublons */}
          {doublonsData && doublonsData.total_doublons > 0 && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700 rounded-xl p-4 flex items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center">
                  <AlertTriangle size={20} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-red-800 dark:text-red-200">
                    {doublonsData.total_doublons} reçu(s) doublon(s) détecté(s)
                  </h3>
                  <p className="text-xs text-red-700 dark:text-red-300 mt-0.5">
                    {doublonsData.groupes.length} groupe(s) avec des reçus identiques (même formateur, mêmes copies, mêmes matières).
                    Les calculs financiers peuvent être faussés.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowDoublonsModal(true)}
                className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 shadow-md"
              >
                <Copy size={14} /> Voir & nettoyer
              </button>
            </div>
          )}

          {/* Cartes de synthèse financière */}
          <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl p-4 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-medium opacity-90 uppercase">Reçus émis</div>
                  <div className="text-3xl font-extrabold mt-1">{totalRecus.nbRecus}</div>
                </div>
                <Receipt size={32} className="opacity-50" />
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl p-4 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-medium opacity-90 uppercase">Formateurs payés</div>
                  <div className="text-3xl font-extrabold mt-1">{totalRecus.formateursDistincts}</div>
                </div>
                <Wallet size={32} className="opacity-50" />
              </div>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-700 rounded-xl p-4 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-medium opacity-90 uppercase">Total copies</div>
                  <div className="text-3xl font-extrabold mt-1">{totalRecus.totalCopies.toLocaleString()}</div>
                </div>
                <FileText size={32} className="opacity-50" />
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-700 rounded-xl p-4 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-medium opacity-90 uppercase">Montant total</div>
                  <div className="text-2xl font-extrabold mt-1">{totalRecus.totalGeneral.toLocaleString()}</div>
                  <div className="text-[10px] opacity-80">FCFA</div>
                </div>
                <TrendingUp size={32} className="opacity-50" />
              </div>
            </div>
          </div>

          {/* Séparation Ordinaire / Rattrapage pour les reçus */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-2">
                Session Ordinaire — {totalRecus.ordinaire.nbRecus} reçu(s)
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <div className="text-gray-500 dark:text-gray-400">Copies</div>
                  <div className="text-base font-bold text-emerald-700 dark:text-emerald-300">{totalRecus.ordinaire.totalCopies.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-gray-500 dark:text-gray-400">Bonus</div>
                  <div className="text-base font-bold text-emerald-700 dark:text-emerald-300">{totalRecus.ordinaire.totalBonus.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-gray-500 dark:text-gray-400">Total</div>
                  <div className="text-base font-extrabold text-emerald-700 dark:text-emerald-300">{totalRecus.ordinaire.totalGeneral.toLocaleString()} <span className="text-[9px] font-normal">FCFA</span></div>
                </div>
              </div>
            </div>
            <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-xl p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-300 mb-2">
                Session de Rattrapage — {totalRecus.rattrapage.nbRecus} reçu(s)
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <div className="text-gray-500 dark:text-gray-400">Copies</div>
                  <div className="text-base font-bold text-red-700 dark:text-red-300">{totalRecus.rattrapage.totalCopies.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-gray-500 dark:text-gray-400">Bonus</div>
                  <div className="text-base font-bold text-red-700 dark:text-red-300">{totalRecus.rattrapage.totalBonus.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-gray-500 dark:text-gray-400">Total</div>
                  <div className="text-base font-extrabold text-red-700 dark:text-red-300">{totalRecus.rattrapage.totalGeneral.toLocaleString()} <span className="text-[9px] font-normal">FCFA</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Filtres + Actions */}
          <div className="mb-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-[10px] text-gray-500 dark:text-gray-400 mb-1 uppercase font-semibold">Recherche</label>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" value={recuSearch} onChange={(e) => setRecuSearch(e.target.value)} placeholder="N° reçu, formateur..."
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 dark:text-gray-400 mb-1 uppercase font-semibold">Date début</label>
                <input type="date" value={recuDateDebut} onChange={(e) => setRecuDateDebut(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700" />
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 dark:text-gray-400 mb-1 uppercase font-semibold">Date fin</label>
                <input type="date" value={recuDateFin} onChange={(e) => setRecuDateFin(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700" />
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 dark:text-gray-400 mb-1 uppercase font-semibold">Session</label>
                <select value={recuSession} onChange={(e) => setRecuSession(e.target.value as any)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700">
                  <option value="">Toutes</option>
                  <option value="normal">Ordinaire</option>
                  <option value="rattrapage">Rattrapage</option>
                  <option value="mixte">Mixte</option>
                </select>
              </div>
              {(recuSearch || recuDateDebut || recuDateFin || recuSession) && (
                <button onClick={() => { setRecuSearch(''); setRecuDateDebut(''); setRecuDateFin(''); setRecuSession(''); }}
                  className="px-3 py-2 text-xs text-gray-500 hover:text-gray-700">
                  Réinit.
                </button>
              )}
              <div className="flex gap-2 ml-auto">
                <button onClick={handleExportRecusExcel} disabled={recusFiltres.length === 0}
                  className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 disabled:opacity-50">
                  <FileSpreadsheet size={14} /> Excel
                </button>
                <button onClick={handleImprimerSynthese} disabled={recusFiltres.length === 0}
                  className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 shadow-md">
                  <Printer size={14} /> Imprimer synthèse
                </button>
              </div>
            </div>
          </div>

          {/* Tableau des reçus */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-purple-200 dark:border-purple-700 overflow-hidden">
            <div className="px-4 py-3 bg-purple-50 dark:bg-purple-900/10 border-b border-purple-200 dark:border-purple-700 flex items-center justify-between">
              <h3 className="text-sm font-bold text-purple-800 dark:text-purple-300 flex items-center gap-2">
                <Receipt size={16} /> Reçus de correction émis
                <span className="text-xs font-normal text-purple-600 dark:text-purple-400">
                  ({recusFiltres.length} sur {recus.length})
                </span>
              </h3>
            </div>
            {recusFiltres.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                <Receipt size={48} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">{recus.length === 0 ? 'Aucun reçu émis' : 'Aucun reçu correspondant aux filtres'}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">N° Reçu</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Date</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Formateur</th>
                      <th className="px-3 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase">Copies</th>
                      <th className="px-3 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase">Bonus</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-gray-500 uppercase">Montant</th>
                      <th className="px-3 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                    {recusFiltres.map(r => {
                      const isDoublon = recusDoublonsIds.has(r.id);
                      return (
                      <tr key={r.id} className={`hover:bg-purple-50/30 dark:hover:bg-purple-900/10 ${isDoublon ? 'bg-red-50/40 dark:bg-red-900/10' : ''}`}>
                        <td className="px-3 py-2.5 text-xs font-mono font-bold text-purple-700 dark:text-purple-300">
                          <div className="flex items-center gap-1.5">
                            {r.numero_recu}
                            {isDoublon && (
                              <span className="px-1.5 py-0.5 bg-red-600 text-white rounded text-[8px] font-bold uppercase" title="Reçu doublon — existe en double avec un autre reçu">
                                Doublon
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-400">{new Date(r.date_emission).toLocaleDateString('fr-FR')}</td>
                        <td className="px-3 py-2.5 text-xs font-medium text-gray-900 dark:text-gray-100">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {r.formateur_nom}
                            {r.type_examen === 'rattrapage' && (
                              <span className="text-[8px] font-bold uppercase px-1 py-0.5 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 rounded">Ratt.</span>
                            )}
                            {r.type_examen === 'mixte' && (
                              <span className="text-[8px] font-bold uppercase px-1 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 rounded">Mixte</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-center text-sm font-bold text-blue-600">{r.total_copies}</td>
                        <td className="px-3 py-2.5 text-center text-xs text-orange-600">
                          {r.bonus > 0 ? `+${r.bonus.toLocaleString()}` : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <div className="text-sm font-bold text-green-600">{r.montant_total.toLocaleString()} FCFA</div>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => handleImprimerRecu(r.id)} className="px-2 py-1 text-[10px] bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded hover:bg-purple-200 font-medium" title="Imprimer ce reçu">
                              <Printer size={12} />
                            </button>
                            <button onClick={() => handleAnnulerRecu(r.id, r.numero_recu)} className="px-2 py-1 text-[10px] bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded hover:bg-orange-200 font-medium" title="Annuler (remet enveloppes en déposées)">
                              <X size={12} />
                            </button>
                            {isDoublon && (
                              <button onClick={() => handleSupprimerDoublon(r.id, r.numero_recu)} className="px-2 py-1 text-[10px] bg-red-600 text-white rounded hover:bg-red-700 font-medium" title="Supprimer ce reçu doublon">
                                <Trash2 size={12} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border-t-2 border-amber-300 dark:border-amber-700">
                      <td colSpan={3} className="px-3 py-3 text-sm font-bold text-amber-800 dark:text-amber-300">TOTAL GÉNÉRAL</td>
                      <td className="px-3 py-3 text-center text-sm font-bold text-blue-700">{totalRecus.totalCopies.toLocaleString()}</td>
                      <td className="px-3 py-3 text-center text-xs font-semibold text-orange-600">{totalRecus.totalBonus.toLocaleString()}</td>
                      <td className="px-3 py-3 text-right text-base font-extrabold text-green-700 dark:text-green-400">{totalRecus.totalGeneral.toLocaleString()} FCFA</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modal Doublons */}
      {showDoublonsModal && doublonsData && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-red-800 dark:text-red-200 flex items-center gap-2">
                  <AlertTriangle size={20} /> Gestion des reçus doublons
                </h3>
                <p className="text-xs text-red-700 dark:text-red-300 mt-0.5">
                  {doublonsData.groupes.length} groupe(s) — {doublonsData.total_doublons} reçu(s) à nettoyer
                </p>
              </div>
              <button onClick={() => setShowDoublonsModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={22} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {doublonsData.groupes.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle2 size={48} className="mx-auto text-green-400 mb-3" />
                  <p className="text-lg text-gray-600 dark:text-gray-300">Aucun doublon détecté</p>
                </div>
              ) : (
                doublonsData.groupes.map((groupe, gIdx) => (
                  <div key={gIdx} className="border-2 border-red-200 dark:border-red-800 rounded-xl overflow-hidden">
                    {/* En-tête du groupe */}
                    <div className="bg-red-50 dark:bg-red-900/10 px-4 py-3 border-b border-red-200 dark:border-red-800">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-bold text-sm text-red-800 dark:text-red-200">
                            {groupe.cle.formateur}
                          </h4>
                          <div className="text-xs text-red-700 dark:text-red-300 mt-1">
                            {groupe.count} reçus identiques · {groupe.cle.total_copies} copies · {groupe.cle.nombre_enveloppes} enveloppe(s)
                          </div>
                          <div className="text-[10px] text-red-600 dark:text-red-400 mt-1 italic">
                            Matières : {groupe.cle.matieres.join(', ') || '—'}
                          </div>
                        </div>
                        <span className="px-2 py-1 bg-red-600 text-white text-xs rounded-full font-bold">
                          {groupe.count}×
                        </span>
                      </div>
                    </div>

                    {/* Liste des reçus du groupe */}
                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                      {groupe.recus.map((recu, rIdx) => {
                        const isOriginal = rIdx === 0;
                        return (
                          <div key={recu.id} className={`px-4 py-3 flex items-center justify-between ${
                            isOriginal ? 'bg-green-50/50 dark:bg-green-900/10' : 'bg-white dark:bg-gray-800'
                          }`}>
                            <div className="flex items-center gap-3">
                              {isOriginal ? (
                                <span className="px-2 py-0.5 bg-green-600 text-white text-[10px] font-bold rounded uppercase">
                                  ✓ Original
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded uppercase">
                                  Doublon #{rIdx}
                                </span>
                              )}
                              <div>
                                <div className="font-mono font-bold text-sm text-purple-700 dark:text-purple-300">
                                  {recu.numero_recu}
                                </div>
                                <div className="text-[10px] text-gray-500 dark:text-gray-400">
                                  Émis le {new Date(recu.date_emission).toLocaleDateString('fr-FR')}
                                  {recu.agent_nom && ` par ${recu.agent_nom}`}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <div className="text-sm font-bold text-green-600">
                                  {recu.montant_total.toLocaleString()} FCFA
                                </div>
                                <div className="text-[10px] text-gray-500">{recu.total_copies} copies</div>
                              </div>
                              {!isOriginal && (
                                <button
                                  onClick={async () => {
                                    await handleSupprimerDoublon(recu.id, recu.numero_recu);
                                  }}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700"
                                >
                                  <Trash2 size={12} /> Supprimer
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 flex items-center justify-between">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                <strong>Conseil :</strong> Le reçu le plus ancien est conservé comme « original ». Supprimez les doublons pour rectifier les calculs.
              </p>
              <button
                onClick={() => setShowDoublonsModal(false)}
                className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal pré-génération du reçu — permet d'ajouter des copies
          supplémentaires (étudiants d'années antérieures venus en rattrapage)
          avant de valider, et affiche un aperçu du montant. */}
      {modalGenerer && (
        <ModalGenererRecu
          info={modalGenerer}
          onClose={() => setModalGenerer(null)}
          onConfirm={confirmerGenererRecu}
          isLoading={generantRecu}
        />
      )}

      {/* Bouton flottant « Retour en haut » */}
      <ScrollToTop threshold={500} />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Modal pré-génération du reçu
// ─────────────────────────────────────────────────────────────────────────────
interface ModalGenererRecuProps {
  info: {
    formateurNom: string;
    formateurId: number | null;
    enveloppeIds: number[];
    nbCopiesEnveloppes: number;
  };
  onClose: () => void;
  onConfirm: (params: {
    copies_supplementaires: number;
    copies_supplementaires_motif: string;
  }) => void;
  isLoading?: boolean;
}

const ModalGenererRecu: React.FC<ModalGenererRecuProps> = ({ info, onClose, onConfirm, isLoading }) => {
  const [aDesCopiesSupp, setADesCopiesSupp] = useState<null | boolean>(null);
  const [nbCopiesSupp, setNbCopiesSupp] = useState<string>('');
  const [motif, setMotif] = useState('');

  const nbSupp = Math.max(0, Number(nbCopiesSupp) || 0);
  const totalCopies = info.nbCopiesEnveloppes + nbSupp;
  const bonus = totalCopies > 0 && totalCopies < 10 ? 1000 : 0;
  const montant = totalCopies * 100 + bonus;

  const canValider =
    aDesCopiesSupp === false ||
    (aDesCopiesSupp === true && nbSupp > 0 && motif.trim().length >= 3);

  return (
    <div className="fixed inset-0 z-[85] bg-black/60 flex items-center justify-center p-3" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-xl shadow-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 max-h-[92vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Générer un reçu — {info.formateurNom}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-xl leading-none">×</button>
        </div>

        <div className="p-5 space-y-4 text-sm text-gray-800 dark:text-gray-200">
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-800/50 space-y-1">
            <div>Enveloppes déposées : <strong>{info.enveloppeIds.length}</strong></div>
            <div>Copies liées aux enveloppes : <strong>{info.nbCopiesEnveloppes}</strong></div>
          </div>

          {/* Étape 1 : proposer l'ajout de copies supplémentaires */}
          {aDesCopiesSupp === null && (
            <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3">
              <div className="font-semibold text-sm mb-1 text-blue-900 dark:text-blue-200">
                Y a-t-il des copies à ajouter en dehors des enveloppes ?
              </div>
              <p className="text-xs text-blue-800 dark:text-blue-300 mb-3">
                Cochez « oui » si des étudiants d'années antérieures sont venus
                composer un rattrapage — ces copies ne sont pas dans le système
                courant, l'agent doit les compter manuellement.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setADesCopiesSupp(true)}
                  className="px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium"
                >
                  Oui, ajouter des copies
                </button>
                <button
                  onClick={() => setADesCopiesSupp(false)}
                  className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium"
                >
                  Non, générer directement
                </button>
              </div>
            </div>
          )}

          {/* Étape 2a : saisir les copies supplémentaires */}
          {aDesCopiesSupp === true && (
            <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-amber-900 dark:text-amber-200 uppercase">
                  Copies supplémentaires
                </div>
                <button
                  onClick={() => { setADesCopiesSupp(false); setNbCopiesSupp(''); setMotif(''); }}
                  className="text-[11px] text-amber-700 dark:text-amber-300 hover:underline"
                >
                  ← Retour
                </button>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-0.5">
                  Nombre de copies à ajouter *
                </label>
                <input
                  type="number"
                  min={1}
                  value={nbCopiesSupp}
                  onChange={(e) => setNbCopiesSupp(e.target.value)}
                  className="w-full px-3 py-1.5 rounded border text-sm bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Ex : 3"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-0.5">
                  Motif (obligatoire — figurera sur le reçu) *
                </label>
                <textarea
                  value={motif}
                  onChange={(e) => setMotif(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-1.5 rounded border text-sm bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Ex : L2AI - Rattrapage 2024-2025 (3 étudiants)"
                />
              </div>
            </div>
          )}

          {/* Résumé + total */}
          {aDesCopiesSupp !== null && (
            <div className="rounded-lg border-2 border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 p-3">
              <div className="text-[11px] font-semibold text-emerald-900 dark:text-emerald-200 uppercase mb-1">
                Aperçu du reçu
              </div>
              <table className="w-full text-xs">
                <tbody>
                  <tr>
                    <td className="py-0.5 text-gray-700 dark:text-gray-300">Copies des enveloppes</td>
                    <td className="py-0.5 text-right tabular-nums">{info.nbCopiesEnveloppes}</td>
                  </tr>
                  {nbSupp > 0 && (
                    <tr>
                      <td className="py-0.5 text-gray-700 dark:text-gray-300">+ Copies supplémentaires</td>
                      <td className="py-0.5 text-right tabular-nums text-amber-700 dark:text-amber-300">+{nbSupp}</td>
                    </tr>
                  )}
                  <tr className="border-t border-emerald-200 dark:border-emerald-800">
                    <td className="py-1 font-semibold">Total copies</td>
                    <td className="py-1 text-right tabular-nums font-semibold">{totalCopies}</td>
                  </tr>
                  <tr>
                    <td className="py-0.5 text-gray-700 dark:text-gray-300">Prix unitaire</td>
                    <td className="py-0.5 text-right tabular-nums">100 FCFA</td>
                  </tr>
                  {bonus > 0 && (
                    <tr>
                      <td className="py-0.5 text-gray-700 dark:text-gray-300">Bonus (&lt; 10 copies)</td>
                      <td className="py-0.5 text-right tabular-nums text-emerald-700 dark:text-emerald-300">+1 000 FCFA</td>
                    </tr>
                  )}
                  <tr className="border-t-2 border-emerald-400 dark:border-emerald-600">
                    <td className="py-1.5 font-bold text-emerald-900 dark:text-emerald-200">MONTANT TOTAL</td>
                    <td className="py-1.5 text-right tabular-nums font-bold text-emerald-900 dark:text-emerald-200 text-base">
                      {montant.toLocaleString('fr-FR')} FCFA
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Annuler
          </button>
          <button
            onClick={() => onConfirm({
              copies_supplementaires: nbSupp,
              copies_supplementaires_motif: motif.trim(),
            })}
            disabled={!canValider || !!isLoading}
            className="px-4 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-50"
          >
            {isLoading ? 'Génération…' : 'Générer le reçu'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuiviEnveloppes;
